
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { TestCallInput, ElevenLabsAgent } from "@/types/admin";
import AgentSelector from "./AgentSelector";
import SavedPhoneNumbers from "./SavedPhoneNumbers";
import TestCallVariables from "./TestCallVariables";
import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface TestCallFormProps {
  agentSource: "local" | "elevenlabs";
  isLoadingElevenLabsAgents: boolean;
  isLoadingLocalAgents: boolean;
  elevenLabsAgents: ElevenLabsAgent[];
  localAgents: any[] | undefined;
  isSubmitting: boolean;
  showNoAgentsAvailable: boolean;
  onSubmit: (values: TestCallInput) => Promise<void>;
}

const formSchema = z.object({
  phoneNumber: z.string().min(10, "Phone number must be at least 10 digits"),
  agentId: z.string().min(1, "Please select an agent"),
  script: z.string().optional(),
});

const TestCallForm = ({
  agentSource,
  isLoadingElevenLabsAgents,
  isLoadingLocalAgents,
  elevenLabsAgents,
  localAgents,
  isSubmitting,
  showNoAgentsAvailable,
  onSubmit,
}: TestCallFormProps) => {
  // Get the default phone number from localStorage if available
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string>(() => {
    const saved = localStorage.getItem("testCallPhoneNumbers");
    if (saved) {
      const numbers = JSON.parse(saved);
      const defaultNumber = numbers.find((num: any) => num.isDefault);
      return defaultNumber ? defaultNumber.number : "";
    }
    return "";
  });
  
  // Add state for variables
  const [variables, setVariables] = useState<Record<string, string>>({
    lead_first_name: "",
    agent_name: "", 
    dealership_name: ""
  });
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phoneNumber: selectedPhoneNumber,
      agentId: "",
      script: "",
    },
  });
  
  // Update form value when phone number is selected
  const handlePhoneNumberSelect = (number: string) => {
    setSelectedPhoneNumber(number);
    form.setValue("phoneNumber", number);
  };

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    // Handle different agent sources
    let agentIdToUse = values.agentId;
    let phoneNumberId: string | undefined;
    
    if (agentSource === "local") {
      // Find the selected agent in local DB to get its ElevenLabs agent ID
      const selectedAgent = localAgents?.find(agent => agent.id === values.agentId);
      
      if (!selectedAgent || !selectedAgent.elevenlabs_agent_id) {
        throw new Error("Selected agent doesn't have an ElevenLabs agent ID configured");
      }
      
      agentIdToUse = selectedAgent.elevenlabs_agent_id;
      // Use the agent's phone_number_id if available
      phoneNumberId = selectedAgent.phone_number_id;
    }

    console.log("Variables before submission:", variables);
    
    // Only include variables that have non-empty values
    const variablesToSubmit = Object.entries(variables)
      .filter(([_, value]) => value.trim() !== '')
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {} as Record<string, string>);
    
    console.log("Variables after filtering:", variablesToSubmit);
    
    // Make sure we're not passing undefined for script when it's empty
    const scriptValue = values.script && values.script.trim() !== "" ? values.script : undefined;
    
    const callInput: TestCallInput = {
      phoneNumber: values.phoneNumber,
      agentId: agentIdToUse,
      script: scriptValue,
      phoneNumberId,
      variables: Object.keys(variablesToSubmit).length > 0 ? variablesToSubmit : undefined
    };
    
    console.log("Full call input payload:", callInput);
    
    await onSubmit(callInput);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <div className="space-y-4">
          <SavedPhoneNumbers 
            onSelectNumber={handlePhoneNumberSelect}
            selectedNumber={selectedPhoneNumber}
          />
          
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="+1234567890"
                    {...field}
                    value={selectedPhoneNumber || field.value} 
                  />
                </FormControl>
                <FormDescription>
                  Enter your phone number in international format (+1XXXXXXXXXX)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="agentId"
          render={({ field }) => (
            <AgentSelector
              agentSource={agentSource}
              field={field}
              isLoadingElevenLabsAgents={isLoadingElevenLabsAgents}
              isLoadingLocalAgents={isLoadingLocalAgents}
              elevenLabsAgents={elevenLabsAgents}
              localAgents={localAgents}
            />
          )}
        />

        <Accordion type="single" collapsible className="w-full" defaultValue="variables">
          <AccordionItem value="variables">
            <AccordionTrigger className="text-sm font-medium">
              Variables (Required)
            </AccordionTrigger>
            <AccordionContent>
              <TestCallVariables 
                variables={variables}
                onChange={setVariables}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <FormField
          control={form.control}
          name="script"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Script Override (Optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Hello, this is a test call from ReheatLeads..."
                  className="min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Leave blank to use the agent's default script. You can use variables like {'{lead_first_name}'}.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button 
          type="submit" 
          className="w-full"
          disabled={isSubmitting || showNoAgentsAvailable}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Initiating Test Call...
            </>
          ) : (
            <>
              <Phone className="mr-2 h-4 w-4" />
              Make Test Call
            </>
          )}
        </Button>
      </form>
    </Form>
  );
};

export default TestCallForm;
